<template>
  <div class="body-block">
    <!-- ===== 深黃色公告 BAR ===== -->
    <DisclaimerBar
      :content="disclaimerContent"
      :isAdmin="isAdmin"
      :isLoading="isLoading"
      @save="handleSaveDisclaimer"
    />

    <!-- 看板區域 -->
    <div class="board-container">
      <board-column
        v-for="column in columns"
        :key="column.id"
        :column="column"
        :action-column="getActionColumn(column)"
        :member-options="memberOptions"
        @add-card="handleAddCard"
        @card-click="openCardDetail"
        @card-toggle-check="toggleCheck"
        @card-archive="archiveCard"
      />
    </div>

    <!-- 子卡詳情彈窗 -->
    <card-detail-dialog
      :isSaving.sync="isSaving"
      :visible.sync="detailDialogVisible"
      :card="activeCard"
      :column-id="activeCardColumnId"
      :columns="columns"
      :member-options="memberOptions"
      @column-switch="handleColumnSwitch"
      @toggle-check="toggleCheck"
    />
  </div>
</template>
<script>
import { getBoards, saveBoards } from '@/api/internalTask'
import DisclaimerBar from '@/components/Bars/DisclaimerBar.vue'
import checkPermission from '@/utils/permission'
import dayjs from 'dayjs'
import draggable from 'vuedraggable'
import BoardColumn from './boardColumn.vue'
import CardDetailDialog from './cardDetailDialog.vue'
import { cloneDeep } from 'lodash-es'
import { getAgencyMembers } from '@/api/member.js'
import { getServiceIntroduction, saveServiceIntroduction } from '@/api/unity.js'
import { base64ToHtml, htmlToBase64 } from '@/utils/funcs.js'

export default {
  name: 'InternalTask',
  components: { draggable, DisclaimerBar, BoardColumn, CardDetailDialog },
  data() {
    return {
      systemConfig: {
        getBoardsInterval: 10000, // 取得看板的間隔時間 (毫秒)
        saveBoardsTimeout: 1000, // 儲存看板的延遲時間 (毫秒)
        SaveCardDetailWaitGetKeyTimeout: 500, // 儲存卡片明細等待先前(新增或編輯)保存資料回傳的延遲時間 (毫秒)
      },
      agencyCode:
        this.$store.getters.userInfo && this.$store.getters.userInfo.agencyCode
          ? this.$store.getters.userInfo.agencyCode
          : null,
      isLoading: false,
      isAdmin: checkPermission(['ITA-ADMIN', 'ITA-DEV', 'ITA-SALES', 'SERVICE-ADMIN']),
      disclaimerContent: '',
      detailDialogVisible: false,
      activeCard: null,
      activeCardColumnId: null,
      memberOptions: [],
      columns: [],
      oldColumns: [],
      actionColumns: [],
      columnsByChangeBefore: [],
      isSaving: false,
      savingTimeout: null,
      isFirstLoad: true,
      getBoardInterval: null,
      isGetBoardIntervalRunning: false,
      runSaveCardDetailTimeout: null,
      isSaveReload: false,
    }
  },
  async created() {
    await this.getDisclaimerContent()
    await this.getMemberOptions()
    await this.getBoards()
    this.isFirstLoad = false

    if (this.getBoardInterval) {
      clearInterval(this.getBoardInterval)
    }

    this.getBoardInterval = setInterval(async () => {
      if (!this.isGetBoardIntervalRunning && !this.isSaving) {
        await this.getBoards()
      }
    }, this.systemConfig.getBoardsInterval)
  },
  mounted() {
    // 監聽瀏覽器關閉/重新整理事件
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)

    if (this.getBoardInterval) {
      clearInterval(this.getBoardInterval)
      this.getBoardInterval = null
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      const answer = window.confirm('資料正在保存中，確定要關閉/離開畫面嗎？')
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  watch: {
    columns: {
      async handler(newVal, oldVal) {
        if (this.isSaveReload) {
          this.isSaveReload = false
          return
        }

        if (!this.isFirstLoad && JSON.stringify(newVal) !== JSON.stringify(this.oldColumns)) {
          /*
          // 差異比對工具
          // const { hasChanges, diff } = this.getColumnsDiff(this.oldColumns, newVal)

          // if (hasChanges) {
          //   console.log('🔍 變更分析報告：', diff)

          //   if (this.detailDialogVisible) {
          //     await this.runSaveCardDetail(newVal)
          //   } else {
          //     await this.runSaveBoardsSetTimeout(newVal)
          //   }
          // }
          */

          if (this.detailDialogVisible) {
            await this.runSaveCardDetail(newVal)
          } else {
            await this.runSaveBoardsSetTimeout(newVal)
          }
        }
      },
      deep: true,
    },
    async agencyCode(newVal) {
      await this.getBoards()
    },
    async isLoading(newVal) {
      if (newVal) {
        try {
          await this.$store.dispatch('app/showDownloadLoading', true)
          await this.$store.dispatch('app/showDownloadLoadingText', '資料處理中，請稍後')
        } catch (error) {
          console.error('Loading 初始化失敗:', error)
          await this.$store.dispatch('app/showDownloadLoading', false)
        }
      } else {
        await this.$store.dispatch('app/showDownloadLoading', false)
      }
    },
  },
  methods: {
    dayjs,
    /**
     * 比對舊 columns 與新 columns 的差異
     * @param {Array} oldCols - 舊的 columns 陣列
     * @param {Array} newCols - 新的 columns 陣列
     * @returns {Object} 包含新增、刪除、修改細節的 report
     */
    getColumnsDiff(oldCols = [], newCols = []) {
      const diff = {
        addedColumns: [],
        removedColumns: [],
        updatedColumns: [],
        addedCards: [],
        removedCards: [],
        updatedCards: [],
      }

      const oldColMap = new Map(oldCols.map(col => [col.id, col]))
      const newColMap = new Map(newCols.map(col => [col.id, col]))

      // 1. 找出被刪除的 Columns
      oldCols.forEach(oldCol => {
        if (!newColMap.has(oldCol.id)) {
          diff.removedColumns.push(oldCol)
        }
      })

      // 2. 檢查新增與修改的 Columns & Cards
      newCols.forEach(newCol => {
        const oldCol = oldColMap.get(newCol.id)

        if (!oldCol) {
          // 新增的 Column
          diff.addedColumns.push(newCol)
        } else {
          // 檢查 Column 本身屬性是否有變（排除 cards 屬性）
          const colChanges = this.getChangedFields(oldCol, newCol, ['cards'])
          if (Object.keys(colChanges).length > 0) {
            diff.updatedColumns.push({
              id: newCol.id,
              changes: colChanges,
            })
          }

          // --- 比對內部的 Cards ---
          const oldCards = oldCol.cards || []
          const newCards = newCol.cards || []
          const oldCardMap = new Map(oldCards.map(c => [c.id, c]))
          const newCardMap = new Map(newCards.map(c => [c.id, c]))

          // 2a. 刪除的 Card
          oldCards.forEach(oldCard => {
            if (!newCardMap.has(oldCard.id)) {
              diff.removedCards.push({ columnId: newCol.id, card: oldCard })
            }
          })

          // 2b. 新增或修改的 Card
          newCards.forEach(newCard => {
            const oldCard = oldCardMap.get(newCard.id)
            if (!oldCard) {
              diff.addedCards.push({ columnId: newCol.id, card: newCard })
            } else {
              const cardChanges = this.getChangedFields(oldCard, newCard)
              if (Object.keys(cardChanges).length > 0) {
                diff.updatedCards.push({
                  columnId: newCol.id,
                  cardId: newCard.id,
                  changes: cardChanges, // 包含 { field: { old: x, new: y } }
                })
              }
            }
          })
        }
      })

      // 判斷整體是否有任何變化
      const hasChanges =
        diff.addedColumns.length > 0 ||
        diff.removedColumns.length > 0 ||
        diff.updatedColumns.length > 0 ||
        diff.addedCards.length > 0 ||
        diff.removedCards.length > 0 ||
        diff.updatedCards.length > 0

      return { hasChanges, diff }
    },

    /**
     * 工具函式：比對單一物件兩個版本的純值欄位差異
     */
    getChangedFields(oldObj, newObj, excludeKeys = []) {
      const changes = {}
      const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

      allKeys.forEach(key => {
        if (excludeKeys.includes(key)) return

        const oldVal = oldObj[key]
        const newVal = newObj[key]

        // 簡易判斷（若有複雜物件/陣列欄位可再擴充）
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          changes[key] = {
            from: oldVal,
            to: newVal,
          }
        }
      })

      return changes
    },

    async runSaveCardDetail(newVal) {
      if (!this.activeCard || !this.activeCard.id) {
        if (this.runSaveCardDetailTimeout) {
          clearTimeout(this.runSaveCardDetailTimeout)
        }

        this.runSaveCardDetailTimeout = setTimeout(async () => {
          if (this.detailDialogVisible) {
            await this.runSaveCardDetail(newVal)
          }
        }, this.systemConfig.SaveCardDetailWaitGetKeyTimeout)
      } else {
        await this.runSaveBoardsSetTimeout(newVal)
      }
    },
    async runSaveBoardsSetTimeout(newVal) {
      this.isSaving = true

      if (this.savingTimeout) {
        clearTimeout(this.savingTimeout)
      }

      this.savingTimeout = setTimeout(async () => {
        await saveBoards(newVal)
          .then(async res => {
            const newColumns = res.data

            this.isSaveReload = true
            this.oldColumns = cloneDeep(newColumns)
            this.columns = cloneDeep(newColumns)

            const currentActiveCardColumn = this.columns.find(
              col => col.id === this.activeCardColumnId
            )

            if (this.detailDialogVisible && currentActiveCardColumn.cards.length > 0) {
              const cardsByNoArchived = currentActiveCardColumn.cards.filter(c => !c.archived)
              let newCard = null

              if (this.activeCard && !this.activeCard.id) {
                newCard = cardsByNoArchived[cardsByNoArchived.length - 1]
              } else if (this.activeCard && this.activeCard.id) {
                newCard = cardsByNoArchived.find(c => c.id === this.activeCard.id) || newCard
              }

              if (newCard) {
                const {
                  dueDateTime,
                  completed,
                  description,
                  attachments,
                  archived,
                  members,
                  activities,
                } = this.activeCard
                Object.assign(newCard, {
                  dueDateTime,
                  completed,
                  description,
                  archived,
                })
                this.activeCard = newCard
              }
            }
            this.isSaving = false
          })
          .catch(err => {
            console.log(err)
            this.isSaving = false
            this.isSaveReload = false
          })
      }, this.systemConfig.saveBoardsTimeout)
    },
    async getBoards() {
      if (this.agencyCode) {
        if (this.isFirstLoad) {
          this.isLoading = true
        }

        this.isGetBoardIntervalRunning = true

        await getBoards({ agencyCode: this.agencyCode })
          .then(res => {
            if (res.success) {
              if (this.isFirstLoad) {
                this.actionColumns = res.data.map(v => ({
                  isAdding: false,
                  newCardTitle: '',
                  ...v,
                }))
                this.columns = cloneDeep(res.data)
              }

              if (!this.isSaving) {
                if (res.data.length !== this.oldColumns.length) {
                  this.actionColumns = res.data.map(v => ({
                    isAdding: false,
                    newCardTitle: '',
                    ...v,
                  }))
                }

                if (JSON.stringify(res.data) !== JSON.stringify(this.oldColumns)) {
                  this.columns = cloneDeep(res.data)
                }
              }

              this.oldColumns = cloneDeep(res.data)
            }
          })
          .finally(() => {
            this.isLoading = false
            this.isGetBoardIntervalRunning = false
          })
      }
    },
    toggleCheck(card) {
      card.completed = !card.completed
    },
    archiveCard(card) {
      if (confirm('確定要刪除卡片嗎?')) {
        card.archived = true
        this.$message.success('卡片已刪除')
      }
    },
    handleAddCard({ column, title }) {
      column.cards.push({
        id: null,
        boardId: column.id,
        title,
        completed: false,
        archived: false,
        dueDateTime: null,
        description: '',
        attachments: [],
        activities: [],
      })
    },
    openCardDetail(card, column) {
      this.activeCard = card
      this.activeCardColumnId = column.id
      this.detailDialogVisible = true
    },
    handleColumnSwitch(newColumnId) {
      let currentCard = null
      this.columns.forEach(col => {
        const idx = col.cards.findIndex(c => c.id === this.activeCard.id)
        if (idx !== -1) {
          currentCard = col.cards.splice(idx, 1)[0]
        }
      })
      const targetCol = this.columns.find(col => col.id === newColumnId)
      if (targetCol && currentCard) {
        targetCol.cards.push(currentCard)
        this.activeCardColumnId = newColumnId
      }
    },
    async getDisclaimerContent() {
      await getServiceIntroduction({
        pageName: 'InternalTaskDisclaimer',
      })
        .then(res => {
          this.disclaimerContent = base64ToHtml(res.data.content)
        })
        .catch(err => {
          console.log(err)
        })
    },
    async handleSaveDisclaimer(content) {
      this.isLoading = true
      try {
        await saveServiceIntroduction({
          pageName: 'InternalTaskDisclaimer',
          content: htmlToBase64(content),
        })
          .then(async res => {
            this.disclaimerContent = content
            this.$message({
              message: '儲存成功',
              type: 'success',
            })
          })
          .catch(err => {
            console.log(err)
            this.$message({
              message: '儲存失敗',
              type: 'error',
            })
          })
          .finally(() => {
            this.isLoading = false
          })
      } finally {
        this.disclaimer.isEditMode = false
        this.isLoading = false
      }
    },
    getActionColumn(column) {
      const index = this.columns.indexOf(column)

      if (this.actionColumns && this.actionColumns.length >= index + 1) {
        return this.actionColumns[index]
      } else {
        return { isAdding: false, newCardTitle: '' }
      }
    },
    // 瀏覽器原生關閉/重新整理的處理函式
    handleBeforeUnload(event) {
      if (this.isSaving) {
        event.preventDefault()
        event.returnValue = '資料正在保存中，確定要關閉畫面嗎？'
        return event.returnValue
      }
    },
    // 取得成員選單
    async getMemberOptions() {
      // 因一般情況旅行社成員不會超過1000人，所以這裡直接拉取前1000筆資料，如有超過再調整
      await getAgencyMembers({
        agencyCode: this.agencyCode,
        sort: '-cUpdateDT',
        page: 1,
        limit: 1000,
      })
        .then(res => {
          if (res.success && res.data) {
            const resOption = res.data.data
            this.memberOptions = resOption
          }
        })
        .catch(err => {
          console.log(err)
        })
    },
  },
}
</script>
<style scoped lang="scss">
.body-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1.5rem;
}

.board-container {
  display: flex;
  gap: 1rem;
  /* background-color: #2c1d3f; */
  /* min-height: 100vh; */
  box-sizing: border-box;
  color: #333333;
  overflow-x: auto;
}
</style>
<style>
i {
  cursor: pointer;
}
</style>
